import { useState, useCallback } from 'react';
import {
  addMeFavoriteItemV1,
  createMeFavoriteListV1,
} from '@/services/platform/api/me-—-favorites/me-—-favorites';
import type { AddFavoriteItemRequest, FavoriteItemDto, FavoriteListDto } from '@/services/platform/api/model';

export interface UseAddFavoriteItemResult {
  isLoading: boolean;
  /** Adds an item to an existing favourite list. */
  addItem: (listGuid: string, payload: AddFavoriteItemRequest) => Promise<FavoriteItemDto | null>;
  /** Creates a new favourite list and returns the created list (or null on failure). */
  createList: (name: string, listType: 'Shop' | 'Events') => Promise<FavoriteListDto | null>;
}

export function useAddFavoriteItem(): UseAddFavoriteItemResult {
  const [isLoading, setIsLoading] = useState(false);

  const addItem = useCallback(
    async (listGuid: string, payload: AddFavoriteItemRequest): Promise<FavoriteItemDto | null> => {
      setIsLoading(true);
      try {
        const response = await addMeFavoriteItemV1(listGuid, payload);
        if (response.status === 200 && response.data) {
          return response.data;
        }
        return null;
      } catch {
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const createList = useCallback(
    async (name: string, listType: 'Shop' | 'Events'): Promise<FavoriteListDto | null> => {
      setIsLoading(true);
      try {
        const response = await createMeFavoriteListV1({ name, listType });
        if (response.status === 201 && response.data) {
          return response.data;
        }
        return null;
      } catch {
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { isLoading, addItem, createList };
}
